import { SENTRY_SCRUB_OPTIONS } from '@gallop/domain/sentry-scrub'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router'
import { router } from './router'
import './index.css'

// A deploy replaces the hashed chunk files; a tab opened before the deploy asks for chunks that no longer
// exist and React Router surfaces "Failed to fetch dynamically imported module". Standard Vite remedy:
// reload once so the tab picks up the new index. Guarded so a genuinely broken chunk cannot loop.
window.addEventListener('vite:preloadError', (event) => {
  const key = 'gallop:reloaded-for-chunk'
  const last = Number(sessionStorage.getItem(key) ?? 0)
  if (Date.now() - last < 30_000)
    return
  sessionStorage.setItem(key, String(Date.now()))
  event.preventDefault()
  window.location.reload()
})

// The Pages default hostname (gallop-staging.pages.dev) is another site from the API, so no session
// cookie can hold there. Send it to the canonical origin; hashed preview deployments are left alone.
const canonical = import.meta.env.VITE_CANONICAL_ORIGIN as string | undefined
if (canonical && window.location.origin !== canonical && /^[a-z0-9-]+\.pages\.dev$/.test(window.location.hostname))
  window.location.replace(canonical + window.location.pathname + window.location.search + window.location.hash)

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined
if (dsn) {
  // ADR 0005: the browser SDK gets the same allowlist scrub as the API; fetch breadcrumbs lose their query strings, console lines never leave.
  void import('@sentry/react').then(Sentry => Sentry.init({ dsn, environment: import.meta.env.MODE, tracesSampleRate: 0.1, ...SENTRY_SCRUB_OPTIONS }))
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
)
